/**
 * LicenseDiskScanner - Camera-based PDF417 scanner for South African
 * vehicle license disks and driver's licenses.
 *
 * Uses CameraModal (built on useCamera) for the camera UI, providing
 * auto-torch (3-state with low-light hysteresis), auto-focus (tap-to-focus),
 * zoom slider, portrait/landscape adaptive layout, and cross-platform
 * camera management — the same features as the vehicle photo capture
 * in VehicleModal.
 *
 * Uses the usePdf417Detection hook for continuous barcode detection
 * (BarcodeDetector API preferred, ZXing fallback for iOS Safari).
 *
 * The scanner auto-detects two barcode types:
 *   - SA vehicle license disk (plain text, %-delimited eNatis MVL/RC format)
 *   - SA driver's license (SADL — 720-byte RSA-encrypted PDF417)
 *
 * Vehicle disk data is parsed into registration, VIN, make, model, etc.
 * Driver's license data is RSA-decrypted and parsed into ID number, surname,
 * license number, vehicle codes, dates, etc.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/security-services/guard-mobile.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/security-services/guard-mobile)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Loader2, AlertCircle, ScanLine, CheckCircle, Keyboard, IdCard } from 'lucide-react';
import { createLogger } from '@/lib/debugLogger';
import { useCamera, CameraModal, usePdf417Detection, LicenseDiskSilhouette } from '@/hooks/useCamera';
import { isSADLBarcode, decodeSADL, type SADLData } from '@/lib/sadl/sadlDecoder';
import { parseRsaIdCard, type RsaIdCardData } from '@/lib/sadl/rsaIdCard';
import { loadPlugin } from '@/lib/capacitorPlugins';
import { waitForForeground } from '@/lib/capacitorLifecycleManager';
import type { BarcodeScannerPlugin } from '@/plugins/BarcodeScanner';

const logger = createLogger('LicenseDiskScanner');

// Module-level constant — avoids creating a new array on every render,
// which would cause usePdf417Detection's internal formatsRef to update
// unnecessarily (the hook already guards against this via ref comparison,
// but keeping a stable reference is good practice).
const DETECTION_FORMATS = ['pdf417', 'qr_code', 'data_matrix', 'code_128', 'code_39', 'ean_13'];

// ---------------------------------------------------------------------------
// Types — vehicle license disk
// ---------------------------------------------------------------------------

export interface SALicenseDiskData {
  /** Discriminator: 'vehicle_disk' for vehicle license disk scans */
  type: 'vehicle_disk';
  raw: string;
  format: 'MVL' | 'RC' | 'unknown';
  registration: string | null;
  vin: string | null;
  make: string | null;
  model: string | null;
  vehicle_class: string | null;
  colour: string | null;
  engine_number: string | null;
  expiry_date: string | null;
}

// ---------------------------------------------------------------------------
// Types — SA driver's license (SADL)
// ---------------------------------------------------------------------------

export interface SADLScanData {
  /** Discriminator: 'drivers_license' for SADL scans */
  type: 'drivers_license';
  /** Base64-encoded raw barcode bytes (for audit/debugging) */
  raw: string;
  /** Parsed SADL fields */
  sadl: SADLData;
}

// ---------------------------------------------------------------------------
// Types — RSA ID card / ID book
// ---------------------------------------------------------------------------

export interface RsaIdCardScanData {
  /** Discriminator: 'rsa_id_card' for smart ID card / ID book scans */
  type: 'rsa_id_card';
  raw: string;
  /** Parsed identity fields (id_book carries the ID number only) */
  id: RsaIdCardData;
}

/** Union type for all barcode scan results from this scanner */
export type BarcodeScanResult = SALicenseDiskData | SADLScanData | RsaIdCardScanData;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LicenseDiskScannerProps {
  /** Called with parsed barcode data (vehicle disk or driver's license) */
  onScan: (data: BarcodeScanResult) => void;
  onClose: () => void;
  /** Expected vehicle registration for match indicator (vehicle disk only) */
  expectedRegistration?: string | null;
  isDark?: boolean;
  /** Filter which barcode type to accept:
   *  - 'auto' (default): accept SADL licences, RSA ID cards and vehicle disks
   *  - 'drivers_license': identity documents — SADL preferred, RSA smart ID
   *    card / ID book barcode also accepted
   *  - 'vehicle_disk': only accept vehicle disks, reject identity documents
   */
  scanMode?: 'auto' | 'drivers_license' | 'vehicle_disk';
  /** Title shown in the camera modal header (default: "Scan License Disk") */
  title?: string;
  /** Subtitle shown in the scanning frame overlay (default: derived from scanMode) */
  subtitle?: string;
}

/**
 * Parse the raw PDF417 text from a SA license disk.
 *
 * SA license disk barcodes use a %-delimited format. The field positions
 * differ between MVL (Motor Vehicle Licence) and RC (Registration Certificate):
 *
 * MVL:  [1]=MVL  [6]=plate  [8]=class  [9]=make  [10]=model
 *       [11]=colour  [12]=VIN  [13]=engine  [14]=expiry
 *
 * RC:   [1]=RC    [6]=plate  [7]=class  [8]=make  [9]=model
 *       [10]=VIN  [11]=engine  [13]=expiry
 *
 * Disc layouts vary between generations — the licence-disc document number
 * (e.g. "MVL1CC57") can occupy the index older layouts reserved for the
 * plate. When `expectedRegistration` is provided (inspection flow) an exact
 * normalized match anywhere in the payload wins over positional extraction.
 * Without it, doc-number-shaped fields (MVL123 / RC456 …) are never treated
 * as a plate.
 */
export function parseSALicenseDiskBarcode(raw: string, expectedRegistration?: string | null): SALicenseDiskData {
  const empty: SALicenseDiskData = {
    type: 'vehicle_disk',
    raw,
    format: 'unknown',
    registration: null,
    vin: null,
    make: null,
    model: null,
    vehicle_class: null,
    colour: null,
    engine_number: null,
    expiry_date: null,
  };

  if (!raw || !raw.trim()) return empty;

  // The barcode text is often URL-encoded and %-delimited.
  // Try splitting by % first (the eNatis format uses % as field separator).
  let parts: string[] = [];

  if (raw.includes('%')) {
    // URL-decode each segment, then split
    try {
      parts = raw.split('%').map((p) => decodeURIComponent(p.replace(/\+/g, ' ')).trim());
    } catch {
      parts = raw.split('%').map((p) => p.trim());
    }
  } else {
    // Some scanners return the decoded text with | or tab delimiters
    parts = raw.split(/[|\t]/).map((p) => p.trim());
  }

  // Filter out empty leading parts
  while (parts.length > 0 && !parts[0]) parts.shift();

  // When the caller knows the expected plate, the surest extraction is a
  // direct normalized match — positional layouts vary between disc
  // generations and the document number can sit in the plate's slot.
  const expectedNorm = expectedRegistration ? normalizePlate(expectedRegistration) : '';
  const matchedExpected = expectedNorm
    ? parts.find((p) => normalizePlate(p) === expectedNorm)
    : undefined;

  // A plate candidate: alphanumeric, 4–10 chars, and NOT a disc/document
  // number like "MVL1CC57" (doc type prefix + digits).
  const isPlateLike = (p: string | undefined | null): p is string =>
    !!p && /^[A-Z0-9]{4,10}$/i.test(p) && !/^(MVL|RC)\d/i.test(p);

  const pickRegistration = (positional: string | undefined): string | null =>
    matchedExpected?.toUpperCase()
    ?? (isPlateLike(positional)
      ? positional.toUpperCase()
      : parts.find(isPlateLike)?.toUpperCase() ?? null);

  // Detect format: MVL or RC
  const formatField = parts[1]?.toUpperCase() || '';
  const isMVL = formatField.includes('MVL');
  const isRC = formatField.includes('RC');

  if (!isMVL && !isRC) {
    // Unknown format — prefer the expected plate, else first plate-like field
    return {
      ...empty,
      registration: pickRegistration(undefined),
    };
  }

  const format: 'MVL' | 'RC' = isMVL ? 'MVL' : 'RC';

  if (isMVL) {
    return {
      type: 'vehicle_disk',
      raw,
      format,
      registration: pickRegistration(parts[6]),
      vin: parts[12] || null,
      make: parts[9] || null,
      model: parts[10] || null,
      vehicle_class: parts[8]?.split('/')[0]?.trim() || null,
      colour: parts[11]?.split('/')[0]?.trim() || null,
      engine_number: parts[13] || null,
      expiry_date: parts[14] || null,
    };
  }

  // RC format
  return {
    type: 'vehicle_disk',
    raw,
    format,
    registration: pickRegistration(parts[6]),
    vin: parts[10] || null,
    make: parts[8] || null,
    model: parts[9] || null,
    vehicle_class: parts[7]?.split('/')[0]?.trim() || null,
    colour: null,
    engine_number: parts[11] || null,
    expiry_date: parts[13] || null,
  };
}

/** Normalize a plate string for comparison: uppercase, no spaces/dashes */
function normalizePlate(plate: string): string {
  return plate.toUpperCase().replace(/[\s\-\.]/g, '').replace(/[^A-Z0-9]/g, '').trim();
}

/** Derive raw bytes from decoded barcode text via Latin-1 (charCodeAt & 0xff).
 *  Byte-compaction PDF417 segments decode 1:1, so this recovers binary
 *  payloads (e.g. SADL) when the decoder doesn't expose rawBytes. */
function textToBytes(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    bytes[i] = text.charCodeAt(i) & 0xff;
  }
  return bytes;
}

/** Convert a hex string (from the native ML Kit scanner) to bytes. */
function hexToBytes(hex: string): Uint8Array | undefined {
  const clean = hex.replace(/[^0-9a-fA-F]/g, '');
  if (clean.length < 2 || clean.length % 2 !== 0) return undefined;
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Convert a Uint8Array to a base64 string (for raw barcode storage) */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return typeof btoa !== 'undefined'
    ? btoa(binary)
    : Buffer.from(bytes).toString('base64');
}

export default function LicenseDiskScanner({
  onScan,
  onClose,
  expectedRegistration,
  isDark = false,
  scanMode = 'auto',
  title: titleProp,
  subtitle: subtitleProp,
}: LicenseDiskScannerProps) {
  const [manualEntry, setManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [lastResult, setLastResult] = useState<BarcodeScanResult | null>(null);
  const [matched, setMatched] = useState(false);
  const [decodeError, setDecodeError] = useState<string | null>(null);

  // ── Scanner engine: 'native' = Android ML Kit plugin, 'web' = CameraModal ──
  // 'checking' while we probe for the native plugin.
  const [engine, setEngine] = useState<'checking' | 'native' | 'web'>('checking');
  const [isNativeScanning, setIsNativeScanning] = useState(false);
  const [nativeError, setNativeError] = useState<string | null>(null);
  const nativeScanLaunchedRef = useRef(false);
  const barcodePluginRef = useRef<BarcodeScannerPlugin | null>(null);

  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const scanPrompt = subtitleProp ?? (
    scanMode === 'drivers_license'
      ? "Point at the licence-card barcode — ID card barcodes also scan"
      : scanMode === 'vehicle_disk'
        ? 'Point at the barcode on the vehicle licence disk'
        : "Point at the licence barcode — vehicle disk, driver's card or ID card"
  );

  /**
   * Handle a decoded barcode. Auto-detects whether it's a SADL driver's
   * license (720-byte encrypted binary) or a vehicle license disk
   * (plain text, %-delimited eNatis format).
   *
   * When `scanMode` is set, only the specified barcode type is accepted:
   *   - 'drivers_license': reject vehicle disk barcodes
   *   - 'vehicle_disk': reject SADL barcodes
   *   - 'auto' (default): accept both
   */
  const handleResult = useCallback(
    (rawText: string, rawBytes?: Uint8Array) => {
      setDecodeError(null);

      // No usable payload at all (e.g. native scan returned nothing)
      if (!rawText && (!rawBytes || rawBytes.length === 0)) {
        setDecodeError('No barcode data received — try again.');
        return;
      }

      // ── Check for SADL driver's license (720-byte encrypted PDF417) ──
      // rawBytes comes from the detector (ZXing getRawBytes, or Latin-1
      // text recovery for PDF417). Fall back to deriving bytes from the
      // text — byte-compaction segments map 1:1 via charCodeAt.
      const effectiveBytes = rawBytes ?? textToBytes(rawText);
      const isSADL = isSADLBarcode(effectiveBytes);

      if (isSADL && scanMode === 'vehicle_disk') {
        // SADL scanned but only vehicle disks are accepted
        setDecodeError("Scanned a driver's license, but a vehicle license disk is required.");
        return;
      }

      if (isSADL) {
        try {
          logger.debug('SADL barcode detected (%d bytes), decrypting...', effectiveBytes.length);
          const sadlData = decodeSADL(effectiveBytes);
          const result: SADLScanData = {
            type: 'drivers_license',
            raw: bytesToBase64(effectiveBytes),
            sadl: sadlData,
          };
          setLastResult(result);
          // SADL doesn't have a vehicle registration to match
          setMatched(true);
          onScanRef.current(result);
          return;
        } catch (err) {
          logger.error('SADL decode failed:', err);
          setDecodeError(
            `Driver's license barcode detected but decryption failed: ${err instanceof Error ? err.message : 'unknown error'}`,
          );
          // Don't return — fall through to try other formats
        }
      }

      // ── RSA ID card / ID book (plain text, pipe-delimited or 13 digits) ──
      // Accepted wherever a driver's licence is — the caller warns that SADL
      // is the preferred/stronger credential.
      if (scanMode !== 'vehicle_disk') {
        const idCard = parseRsaIdCard(rawText);
        if (idCard) {
          const result: RsaIdCardScanData = {
            type: 'rsa_id_card',
            raw: rawText,
            id: idCard,
          };
          setLastResult(result);
          setMatched(true);
          onScanRef.current(result);
          return;
        }
      }

      if (scanMode === 'drivers_license') {
        // Vehicle disk scanned but only identity documents are accepted.
        // A very long payload means we decoded a binary barcode (SADL)
        // whose bytes were mangled in transport — not a vehicle disk.
        setDecodeError(
          rawText.length >= 400
            ? "Driver's license barcode detected but its data couldn't be read — hold the barcode steady in good light and try again."
            : 'Scanned a vehicle license disk, but an identity document (driver\'s licence or ID) is required.',
        );
        return;
      }

      // ── Vehicle license disk (plain text PDF417) ──
      const parsed = parseSALicenseDiskBarcode(rawText, expectedRegistration);

      // A long payload that parses as 'unknown' with no registration is
      // almost certainly a binary barcode (e.g. SADL) whose bytes were
      // mangled — don't surface it as a bogus "verified" disk scan.
      if (parsed.format === 'unknown' && !parsed.registration && rawText.length >= 400) {
        setDecodeError('PDF417 barcode detected but its data could not be read — hold steady and try again.');
        return;
      }

      setLastResult(parsed);

      if (expectedRegistration && parsed.registration) {
        const match = normalizePlate(parsed.registration) === normalizePlate(expectedRegistration);
        setMatched(match);
      } else {
        setMatched(true);
      }

      onScanRef.current(parsed);
    },
    [expectedRegistration, scanMode],
  );

  // ── Scanner engine detection ──
  // Prefer the native ML Kit scanner (Capacitor plugin) — it decodes dense
  // PDF417 far more reliably than the in-browser BarcodeDetector/ZXing path
  // and returns raw bytes (required for SADL decryption). Falls back to the
  // CameraModal web scanner when the plugin isn't available (web browser,
  // iOS without the plugin, or plugin load failure).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plugin = await loadPlugin('BarcodeScanner');
        if (!plugin) {
          if (!cancelled) setEngine('web');
          return;
        }
        const { available } = await plugin.isAvailable();
        if (cancelled) return;
        if (available) {
          barcodePluginRef.current = plugin;
          setEngine('native');
        } else {
          setEngine('web');
        }
      } catch (err) {
        logger.warn('Native barcode scanner unavailable — using camera fallback:', err);
        if (!cancelled) setEngine('web');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Native ML Kit scan (Android) ──
  // Single-shot: launches the native scanner activity, feeds the result
  // through the same handleResult pipeline as the web path. rawBytes
  // arrives as a hex string from the native side.
  const runNativeScan = useCallback(async () => {
    setNativeError(null);
    setDecodeError(null);
    setIsNativeScanning(true);
    try {
      const plugin = barcodePluginRef.current ?? (await loadPlugin('BarcodeScanner'));
      if (!plugin) {
        setEngine('web');
        return;
      }
      barcodePluginRef.current = plugin;

      const perm = await plugin.checkPermission().catch(() => ({ granted: false }));
      if (!perm.granted) {
        const req = await plugin.requestPermission().catch(() => ({ granted: false }));
        if (!req.granted) {
          setNativeError('Camera permission denied. Enable camera access in settings, or use the in-app camera scanner instead.');
          return;
        }
      }

      const result = await plugin.scan({
        formats: ['PDF_417'],
        prompt: scanPrompt,
        showFlashlight: true,
        autoTorch: true,
        overlayShape: scanMode === 'drivers_license' ? 'card' : 'disk',
      });

      if (result.cancelled) return;

      // The scan activity's result resolves via onActivityResult BEFORE
      // onResume propagates to the WebView — so the app may still be flagged
      // backgrounded (3s grace elapsed during the scan) and document may
      // still report 'hidden' for a few ms. Wait for the foreground
      // transition so downstream API calls from the onScan handler aren't
      // short-circuited by the fetch patch's background guard.
      await waitForForeground();

      const bytes = result.rawBytes ? hexToBytes(result.rawBytes) : undefined;
      handleResult(result.content ?? '', bytes);
    } catch (err) {
      logger.error('Native barcode scan failed:', err);
      setNativeError(err instanceof Error ? err.message : 'Native scan failed — try again or use the in-app camera scanner.');
    } finally {
      setIsNativeScanning(false);
    }
  }, [handleResult, scanPrompt, scanMode]);

  // Auto-launch the native scanner once when native mode activates.
  // The ref stays set afterwards — after a cancel/failure the launcher UI
  // stays up and the user retries manually via the Scan button (an
  // immediate auto-relaunch would loop forever on cancels).
  useEffect(() => {
    if (engine === 'native' && !lastResult && !manualEntry && !nativeScanLaunchedRef.current) {
      nativeScanLaunchedRef.current = true;
      void runNativeScan();
    }
  }, [engine, lastResult, manualEntry, runNativeScan]);

  // ── useCamera handles all camera management: stream, torch, permissions ──
  // CameraModal provides auto-torch (darkModeEnhance), zoom slider,
  // tap-to-focus, portrait/landscape layout, and camera switching.
  const camera = useCamera({
    initialFacingMode: 'environment',
    enableTorchByDefault: true,
    preferNativeCameraOnCapacitor: false,
    quality: 0.85,
    // Dense PDF417 barcodes need more pixels per module than the 720p
    // photo-capture default provides — request 1080p for scanning.
    videoWidth: 1920,
    videoHeight: 1080,
    onCapture: () => {
      // Not used for scanning — usePdf417Detection handles decode
    },
    onClose: () => {
      // Camera closed — nothing extra to do, CameraModal handles UI
    },
  });

  const { isOpen, open, close, videoRef, stream, error: cameraError, isLoading } = camera;

  // ── Continuous PDF417 barcode detection ──
  // Runs BarcodeDetector/ZXing on the live video stream. Automatically
  // pauses when the camera is closed or the user is in manual entry mode.
  usePdf417Detection({
    videoRef,
    stream,
    enabled: isOpen && !manualEntry && !lastResult,
    formats: DETECTION_FORMATS,
    onResult: (rawText, rawBytes) => {
      logger.debug('Barcode decoded (%d bytes text, %d raw bytes)', rawText.length, rawBytes?.length ?? 0);
      handleResult(rawText, rawBytes);
    },
  });

  // Open camera on mount (unless manual entry mode or native scanner)
  useEffect(() => {
    if (engine === 'web' && !manualEntry && !isOpen && !lastResult) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualEntry, engine]);

  // Close camera when component unmounts
  useEffect(() => {
    return () => {
      close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    close();
    onClose();
  }, [close, onClose]);

  const handleManualSubmit = useCallback(() => {
    if (manualCode.trim()) {
      handleResult(manualCode.trim());
    }
  }, [manualCode, handleResult]);

  const handleRetry = useCallback(() => {
    setLastResult(null);
    setMatched(false);
    setDecodeError(null);
    if (!isOpen) {
      open();
    }
  }, [isOpen, open]);

  const handleSwitchToCamera = useCallback(() => {
    setManualEntry(false);
    setLastResult(null);
    setMatched(false);
    setDecodeError(null);
    // Explicit user choice for the in-app camera — force web engine
    setEngine('web');
    if (!isOpen) {
      open();
    }
  }, [isOpen, open]);

  const handleSwitchToManual = useCallback(() => {
    setManualEntry(true);
    close();
  }, [close]);

  // ── Custom capture button for CameraModal ──
  // Replaces the default photo capture button with a "Manual Entry" button
  // that switches to manual barcode text entry.
  const customCaptureButton = (
    <button
      type="button"
      onClick={handleSwitchToManual}
      className="flex items-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-xl font-medium hover:bg-gray-600 transition-colors"
    >
      <Keyboard className="w-5 h-5" />
      Manual Entry
    </button>
  );

  // ── Result overlay (rendered on top of CameraModal via portal) ──
  const resultOverlay = lastResult && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[310] flex items-center justify-center bg-gray-900/95 p-6">
      <div className={`max-w-md w-full rounded-2xl border p-6 ${matched ? 'border-green-500 bg-green-900/20' : 'border-amber-500 bg-amber-900/20'}`}>
        <div className="flex items-center gap-3 mb-4">
          {matched ? (
            <CheckCircle className="w-8 h-8 text-green-400" />
          ) : (
            <AlertCircle className="w-8 h-8 text-amber-400" />
          )}
          <h3 className="text-lg font-bold text-white">
            {lastResult.type === 'drivers_license'
              ? (matched ? "Driver's License Scanned" : 'Scan Issue')
              : lastResult.type === 'rsa_id_card'
                ? (matched ? 'ID Document Scanned' : 'Scan Issue')
                : (matched ? 'License Disk Verified' : 'Registration Mismatch')}
          </h3>
        </div>

        {lastResult.type === 'rsa_id_card' ? (
          // ── RSA ID card / ID book fields ──
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">ID Number:</span>
              <span className="text-white font-mono font-semibold">{lastResult.id.idNumber}</span>
            </div>
            {lastResult.id.surname && (
              <div className="flex justify-between">
                <span className="text-gray-400">Surname:</span>
                <span className="text-white">{lastResult.id.surname}</span>
              </div>
            )}
            {lastResult.id.givenNames && (
              <div className="flex justify-between">
                <span className="text-gray-400">Names:</span>
                <span className="text-white">{lastResult.id.givenNames}</span>
              </div>
            )}
            {lastResult.id.gender && (
              <div className="flex justify-between">
                <span className="text-gray-400">Gender:</span>
                <span className="text-white">{lastResult.id.gender === 'M' ? 'Male' : 'Female'}</span>
              </div>
            )}
            {lastResult.id.birthDate && (
              <div className="flex justify-between">
                <span className="text-gray-400">Date of Birth:</span>
                <span className="text-white">{lastResult.id.birthDate}</span>
              </div>
            )}
            {lastResult.id.citizenship && (
              <div className="flex justify-between">
                <span className="text-gray-400">Citizenship:</span>
                <span className="text-white">{lastResult.id.citizenship}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-400">Document:</span>
              <span className="text-white">{lastResult.id.format === 'smart_id' ? 'Smart ID card' : 'ID book barcode'}</span>
            </div>
          </div>
        ) : lastResult.type === 'drivers_license' ? (
          // ── SADL driver's license fields ──
          <div className="space-y-2 text-sm">
            {lastResult.sadl.idNumber && (
              <div className="flex justify-between">
                <span className="text-gray-400">ID Number:</span>
                <span className="text-white font-mono font-semibold">{lastResult.sadl.idNumber}</span>
              </div>
            )}
            {lastResult.sadl.surname && (
              <div className="flex justify-between">
                <span className="text-gray-400">Surname:</span>
                <span className="text-white">{lastResult.sadl.surname}</span>
              </div>
            )}
            {lastResult.sadl.initials && (
              <div className="flex justify-between">
                <span className="text-gray-400">Initials:</span>
                <span className="text-white">{lastResult.sadl.initials}</span>
              </div>
            )}
            {lastResult.sadl.gender && (
              <div className="flex justify-between">
                <span className="text-gray-400">Gender:</span>
                <span className="text-white">{lastResult.sadl.gender === '01' ? 'Male' : 'Female'}</span>
              </div>
            )}
            {lastResult.sadl.birthDate && (
              <div className="flex justify-between">
                <span className="text-gray-400">Date of Birth:</span>
                <span className="text-white">{lastResult.sadl.birthDate}</span>
              </div>
            )}
            {lastResult.sadl.licenseNumber && (
              <div className="flex justify-between">
                <span className="text-gray-400">License No:</span>
                <span className="text-white font-mono">{lastResult.sadl.licenseNumber}</span>
              </div>
            )}
            {lastResult.sadl.licenseValidityStart && (
              <div className="flex justify-between">
                <span className="text-gray-400">Valid From:</span>
                <span className="text-white">{lastResult.sadl.licenseValidityStart}</span>
              </div>
            )}
            {lastResult.sadl.licenseValidityExpiry && (
              <div className="flex justify-between">
                <span className="text-gray-400">Valid Until:</span>
                <span className="text-white">{lastResult.sadl.licenseValidityExpiry}</span>
              </div>
            )}
            {lastResult.sadl.vehicleLicenses.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Codes:</span>
                <span className="text-white font-mono">
                  {lastResult.sadl.vehicleLicenses.map(v => v.code).join(', ')}
                </span>
              </div>
            )}
            {lastResult.sadl.idCountryOfIssue && (
              <div className="flex justify-between">
                <span className="text-gray-400">ID Country:</span>
                <span className="text-white">{lastResult.sadl.idCountryOfIssue}</span>
              </div>
            )}
            {lastResult.sadl.version && (
              <div className="flex justify-between">
                <span className="text-gray-400">Barcode Version:</span>
                <span className="text-white">V{lastResult.sadl.version}</span>
              </div>
            )}
          </div>
        ) : (
          // ── Vehicle license disk fields ──
          <div className="space-y-2 text-sm">
            {lastResult.registration && (
              <div className="flex justify-between">
                <span className="text-gray-400">Registration:</span>
                <span className="text-white font-mono font-semibold">{lastResult.registration}</span>
              </div>
            )}
            {lastResult.vin && (
              <div className="flex justify-between">
                <span className="text-gray-400">VIN:</span>
                <span className="text-white font-mono">{lastResult.vin}</span>
              </div>
            )}
            {lastResult.make && (
              <div className="flex justify-between">
                <span className="text-gray-400">Make:</span>
                <span className="text-white">{lastResult.make}</span>
              </div>
            )}
            {lastResult.model && (
              <div className="flex justify-between">
                <span className="text-gray-400">Model:</span>
                <span className="text-white">{lastResult.model}</span>
              </div>
            )}
            {lastResult.vehicle_class && (
              <div className="flex justify-between">
                <span className="text-gray-400">Class:</span>
                <span className="text-white">{lastResult.vehicle_class}</span>
              </div>
            )}
            {lastResult.colour && (
              <div className="flex justify-between">
                <span className="text-gray-400">Colour:</span>
                <span className="text-white">{lastResult.colour}</span>
              </div>
            )}
            {lastResult.expiry_date && (
              <div className="flex justify-between">
                <span className="text-gray-400">Expiry:</span>
                <span className="text-white">{lastResult.expiry_date}</span>
              </div>
            )}
            {lastResult.format !== 'unknown' && (
              <div className="flex justify-between">
                <span className="text-gray-400">Format:</span>
                <span className="text-white">{lastResult.format}</span>
              </div>
            )}
          </div>
        )}

        {decodeError && (
          <p className="mt-3 text-xs text-amber-400">{decodeError}</p>
        )}

        {!matched && expectedRegistration && lastResult.type === 'vehicle_disk' && (
          <p className="mt-3 text-xs text-amber-400">
            Expected "{expectedRegistration}" but disk reads "{lastResult.registration}"
          </p>
        )}

        <button
          onClick={handleClose}
          className="mt-4 w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Done
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Error overlay (rendered on top of CameraModal via portal) ──
  const errorOverlay = cameraError && !lastResult && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[310] flex items-center justify-center bg-gray-900 p-6">
      <div className="text-center max-w-sm">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-300 text-sm mb-4">{cameraError}</p>
        <button
          onClick={handleRetry}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
        >
          <Camera className="w-4 h-4" />
          Try Again
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Manual entry overlay (rendered on top of CameraModal via portal) ──
  const manualEntryOverlay = manualEntry && !lastResult && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[310] flex flex-col bg-gray-900">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-gray-900/90 text-white"
        style={{
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          paddingBottom: 'max(16px, env(safe-area-inset-top))',
        }}
      >
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Keyboard className="w-5 h-5" />
          Manual Entry
        </h2>
        <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Manual entry area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <p className="text-gray-400 text-sm mb-4 text-center">
          Enter the barcode text manually (from the PDF417 barcode on the license disk)
        </p>
        <textarea
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          placeholder="Paste barcode text here..."
          rows={4}
          className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-blue-500 focus:outline-none font-mono text-sm"
          autoFocus
        />
        <button
          onClick={handleManualSubmit}
          disabled={!manualCode.trim()}
          className="mt-4 w-full py-3 bg-blue-600 text-white rounded-xl font-medium disabled:opacity-50"
        >
          Parse & Verify
        </button>
      </div>

      {/* Bottom toggle */}
      <div
        className="p-4 bg-gray-900/90 flex justify-center gap-2"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={handleSwitchToCamera}
          className="flex items-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-xl font-medium hover:bg-gray-600"
        >
          <Camera className="w-5 h-5" />
          Use Camera
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Loading overlay (rendered on top of CameraModal via portal) ──
  const loadingOverlay = isLoading && !cameraError && !lastResult && !manualEntry && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[305] flex items-center justify-center bg-black/80">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
        <p className="text-gray-300">Starting camera...</p>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Engine-checking overlay — shown briefly while probing for the
  // native ML Kit plugin (avoids flashing the web camera first) ──
  const checkingOverlay = engine === 'checking' && !lastResult && !manualEntry && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[305] flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
        <p className="text-gray-300">Preparing scanner...</p>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Native scanner launcher (Android ML Kit) ──
  // Shown before/after the native scanner activity — auto-launches on entry,
  // and stays available for retry, camera fallback, or manual entry.
  const nativeLauncherOverlay = engine === 'native' && !isNativeScanning && !lastResult && !manualEntry && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[301] flex flex-col bg-gray-900">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-gray-900/90 text-white"
        style={{
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          paddingBottom: 'max(16px, env(safe-area-inset-top))',
        }}
      >
        <h2 className="text-lg font-bold flex items-center gap-2">
          <ScanLine className="w-5 h-5" />
          {titleProp ?? 'Scan License'}
        </h2>
        <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
        <div className="w-20 h-20 rounded-2xl bg-blue-900/30 flex items-center justify-center">
          {scanMode === 'drivers_license' ? (
            <IdCard className="w-10 h-10 text-blue-400" />
          ) : (
            <ScanLine className="w-10 h-10 text-blue-400" />
          )}
        </div>
        <p className="text-gray-300 text-sm text-center max-w-xs">{scanPrompt}</p>

        {decodeError && (
          <p className="text-amber-400 text-xs text-center max-w-xs">{decodeError}</p>
        )}
        {nativeError && (
          <p className="text-red-400 text-xs text-center max-w-xs">{nativeError}</p>
        )}

        <button
          onClick={() => void runNativeScan()}
          className="w-full max-w-xs py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
        >
          <ScanLine className="w-5 h-5" />
          Scan Barcode
        </button>
        <button
          onClick={() => setEngine('web')}
          className="w-full max-w-xs py-2.5 text-sm text-gray-400 hover:text-gray-200 border border-gray-600 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Camera className="w-4 h-4" />
          Use in-app camera instead
        </button>
        <button
          onClick={() => setManualEntry(true)}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1.5"
        >
          <Keyboard className="w-3.5 h-3.5" />
          Enter code manually
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Scanning frame overlay (rendered on top of CameraModal video) ──
  // Shows a guide frame indicating where to position the document:
  //  - drivers_license: rectangular card outline (credit card shape)
  //  - vehicle_disk / auto: circular disk outline with barcode area marker
  const scanningFrameOverlay = isOpen && !lastResult && !cameraError && !manualEntry && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[301] pointer-events-none">
      <div className="absolute inset-0 bg-black/40" />
      {scanMode === 'drivers_license' ? (
        // ── Rectangular card guide for driver's license ──
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div
            className="relative rounded-2xl border-2 border-white/80 overflow-hidden"
            style={{
              width: 'min(85vw, 340px)',
              height: 'min(54vw, 214px)',
              aspectRatio: '1.586 / 1',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
            }}
          >
            {/* Corner markers on the barcode area (lower portion of card) */}
            <div className="absolute left-[5%] top-[55%] h-[30%] w-[90%]">
              <div className="absolute -top-0.5 -left-0.5 w-5 h-5 border-t-[3px] border-l-[3px] border-blue-400 rounded-tl-lg" />
              <div className="absolute -top-0.5 -right-0.5 w-5 h-5 border-t-[3px] border-r-[3px] border-blue-400 rounded-tr-lg" />
              <div className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-b-[3px] border-l-[3px] border-blue-400 rounded-bl-lg" />
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-b-[3px] border-r-[3px] border-blue-400 rounded-br-lg" />
              {/* Scanning line — moves horizontally across the barcode area */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-blue-400"
                style={{ left: '50%', animation: 'cardScanLine 2s ease-in-out infinite' }}
              />
            </div>
          </div>
        </div>
      ) : (
        // ── Circular disk guide for vehicle license disk ──
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {/* Outer circle — the disk boundary */}
          <div
            className="relative rounded-full border-2 border-white/80"
            style={{
              width: 'min(70vw, 280px)',
              height: 'min(70vw, 280px)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
            }}
          >
            {/* Disk silhouette — shows where the barcode is on the disk */}
            <div className="absolute inset-0 flex items-center justify-center p-4 text-white/30">
              <LicenseDiskSilhouette />
            </div>

            {/* Corner markers on the barcode rectangle */}
            <div className="absolute left-[27.5%] top-[47.5%] h-[19%] w-[45%]">
              <div className="absolute -top-0.5 -left-0.5 w-5 h-5 border-t-[3px] border-l-[3px] border-blue-400 rounded-tl-lg" />
              <div className="absolute -top-0.5 -right-0.5 w-5 h-5 border-t-[3px] border-r-[3px] border-blue-400 rounded-tr-lg" />
              <div className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-b-[3px] border-l-[3px] border-blue-400 rounded-bl-lg" />
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-b-[3px] border-r-[3px] border-blue-400 rounded-br-lg" />
              {/* Scanning line — moves vertically across the barcode area */}
              <div
                className="absolute left-0 right-0 h-0.5 bg-blue-400"
                style={{ top: '50%', animation: 'diskScanLine 2s ease-in-out infinite' }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Decode error feedback — shown when a barcode decodes but is the
          wrong type or can't be parsed. Previously this was only rendered
          inside the result overlay (which never mounts on decode failures),
          so users saw no feedback at all. */}
      {decodeError && (
        <div className="absolute bottom-16 left-0 right-0 flex justify-center px-6">
          <span className="px-4 py-2 bg-red-900/80 border border-red-500/60 text-red-100 text-xs rounded-lg text-center max-w-sm">
            {decodeError}
          </span>
        </div>
      )}
      {/* Prompt */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <span className="px-4 py-2 bg-black/60 text-white text-sm rounded-full">
          {scanPrompt}
        </span>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes diskScanLine {
          0%, 100% { transform: translateY(-15px); opacity: 0.5; }
          50% { transform: translateY(15px); opacity: 1; }
        }
        @keyframes cardScanLine {
          0%, 100% { transform: translateX(-120px); opacity: 0.5; }
          50% { transform: translateX(120px); opacity: 1; }
        }
      `}} />
    </div>,
    document.body,
  ) : null;

  // Don't render anything if manual entry is active and no result yet
  // (the manual entry overlay handles its own full-screen UI)
  if (manualEntry && !lastResult) {
    return (
      <>
        {manualEntryOverlay}
        {/* Keep CameraModal mounted but hidden so camera stays alive for quick switch back */}
        <div className="hidden">
          <CameraModal
            camera={camera}
            title={titleProp ?? 'Scan License Disk'}
            darkModeEnhance
            autoCapture={false}
            autoZoom={false}
            reviewCapture={false}
            cropToGuide={false}
            captureButton={customCaptureButton}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/* ── CameraModal provides: auto-torch (darkModeEnhance), zoom slider,
              tap-to-focus, portrait/landscape layout, camera switching ── */}
      <CameraModal
        camera={camera}
        title={titleProp ?? 'Scan License Disk'}
        darkModeEnhance
        autoCapture={false}
        autoZoom={false}
        reviewCapture={false}
        cropToGuide={false}
        captureButton={customCaptureButton}
      />

      {/* Overlays rendered on top of CameraModal */}
      {checkingOverlay}
      {nativeLauncherOverlay}
      {scanningFrameOverlay}
      {loadingOverlay}
      {errorOverlay}
      {resultOverlay}
      {manualEntryOverlay}
    </>
  );
}
